import { Card } from "../types";
import { OUTDATED_THRESHOLD_DAYS, CARD_STATUS } from "../constants/card";

/**
 * Checks if a card is marked as unavailable based on its status
 * @param card - The card to check
 * @returns true if the card status is unavailable (1) or occupied (4)
 */
export const isCardUnavailable = (card: Card): boolean =>
  card.status === CARD_STATUS.UNAVAILABLE ||
  card.status === CARD_STATUS.OCCUPIED;

/**
 * Checks if a card is outdated based on days since last update
 * Prioritizes backend calculation when available (city-specific thresholds)
 * Falls back to hardcoded threshold if backend value not present
 * @param card - The card to check
 * @returns true if the card is outdated based on city-specific or default threshold
 */
export const isCardOutdated = (card: Card): boolean => {
  // Use backend calculation if available (city-specific threshold)
  if (card.is_outdated !== undefined) {
    return card.is_outdated;
  }

  // Fallback to manual calculation with hardcoded threshold
  if (!card.days_since_update) {
    return false;
  }

  const daysSinceUpdate = parseInt(card.days_since_update, 10);

  // Validate parsed number
  if (isNaN(daysSinceUpdate) || daysSinceUpdate < 0) {
    return false;
  }

  return daysSinceUpdate > OUTDATED_THRESHOLD_DAYS;
};

/**
 * Checks if a card should be filtered out based on unavailable or outdated status
 * @param card - The card to check
 * @returns true if the card is either unavailable or outdated
 */
export const isCardUnavailableOrOutdated = (card: Card): boolean =>
  isCardUnavailable(card) || isCardOutdated(card);
